/**
 * Build and URL flags. They are read once at startup and never change during a session.
 *
 * | Export               | Source                        | Effect                                                         |
 * | -------------------- | ----------------------------- | -------------------------------------------------------------- |
 * | `IS_DEV`             | `import.meta.env.DEV`         | `true` under `vite` (dev server), `false` in builds            |
 * | `isDebug`            | `?debug`                      | Shows the Leva panel and exposes `window.__vk` in any build     |
 * | `playgroundSection`  | `?section=<id>`               | Renders a single section (see `App.tsx`)                       |
 * | `forceReducedMotion` | `?motion=reduced`             | Behaves as if `prefers-reduced-motion: reduce` were set        |
 * | `qualityOverride`    | `?quality=low\|medium\|high`  | Pins the WebGL quality tier and disables automatic degrading   |
 *
 * @module
 */
import type { QualityTier } from './quality'

const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search)

/** `true` when running under the Vite dev server. */
export const IS_DEV: boolean = import.meta.env.DEV

/** `?debug` (any value except `0` or `false`) turns on debug tooling. */
export const isDebug: boolean =
  params.has('debug') && params.get('debug') !== '0' && params.get('debug') !== 'false'

/**
 * `?section=<id>` puts the app in playground mode: only that section renders, the phase
 * starts at `'ready'` and there is no preloader. It is `null` when the param is absent.
 * An empty or unknown id shows a dev index of every section.
 */
export const playgroundSection: string | null = params.get('section')

/** `?motion=reduced` forces reduced motion regardless of the OS setting. */
export const forceReducedMotion: boolean = params.get('motion') === 'reduced'

/** `?quality=low|medium|high` pins the quality tier; `null` means auto-detect. */
export const qualityOverride: QualityTier | null = parseQualityTier(params.get('quality'))

function parseQualityTier(value: string | null): QualityTier | null {
  return value === 'low' || value === 'medium' || value === 'high' ? value : null
}
